import { useSearchParams } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardBody,
  MetricCard,
  InsightCard,
  DataTable,
  Chart,
  ChartLegend,
  DashboardSkeleton,
  PageError,
  SampleDataBanner,
} from "../components/index.js";
import { useDashboardData } from "../hooks/useDashboardData.js";
import { rangeDays } from "../lib/time.js";

export function Ads() {
  const { data, loading, error, refetch } = useDashboardData("ads");
  const [searchParams] = useSearchParams();
  const days = rangeDays(searchParams.get("from"), searchParams.get("to"));

  if (loading) return <DashboardSkeleton />;
  if (error)   return <PageError error={error} onRetry={refetch} />;
  if (!data)   return null;

  const { metrics, campaigns, dailySpend, dailySpendEstimated, keywords, adCities, insights, period, conversionsNote } = data;

  const spendSeries = [
    { key: "search", color: "#ff6804", label: "Search" },
    { key: "perf",   color: "#a3a3a3", label: "Performance Max" },
  ];
  const anyEstimated = dailySpendEstimated?.search || dailySpendEstimated?.perf;

  return (
    <div className="space-y-5 max-w-[1400px]">
      {period?.hasRealData === false && <SampleDataBanner />}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <MetricCard {...metrics.totalSpend} />
        <MetricCard {...metrics.totalClicks} />
        <MetricCard {...metrics.avgCpc} />
        <MetricCard {...metrics.dailyAvgSpend} />
        <MetricCard {...metrics.phoneCalls} />
      </div>

      {/*
        Campaign Summary Cards — per-campaign totals live above the daily
        chart so the hard numbers are always visible, even when Windsor's
        daily granularity is unreliable (Performance Max rollups are the
        usual offender). Each card: total spend, daily avg, clicks, CPC,
        share-of-spend bar.
      */}
      <div className={`grid grid-cols-1 md:grid-cols-2 ${campaigns.length > 2 ? "lg:grid-cols-3" : ""} gap-4`}>
        {campaigns.map((c) => (
          <CampaignSummaryCard key={c.campaign} campaign={c} />
        ))}
      </div>

      <Card>
        <CardHeader
          title="Daily Ad Spend"
          subtitle={anyEstimated
            ? `Stacked by campaign · ${days}-day window · estimated where daily granularity was unavailable`
            : `Stacked by campaign · ${days}-day window`}
          right={<ChartLegend items={spendSeries.map((s) => ({ label: s.label, color: s.color }))} />}
        />
        <CardBody>
          {/*
            Stacked area so Performance Max stays readable. As a line chart,
            PMax's smaller spend flatlined against Search's scale. Stacking
            preserves total daily spend at a glance and gives each campaign
            its own visible band. For campaigns where Windsor returns no
            daily breakdown, the aggregator spreads the total evenly across
            the window and flags the series via dailySpendEstimated — the
            card subtitle surfaces that caveat.
          */}
          <Chart
            type="area"
            stacked
            data={dailySpend}
            xKey="date"
            series={spendSeries}
            height={220}
            yFormatter={(v) => `$${v}`}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Campaigns"
          subtitle={`${campaigns.length} paid ${campaigns.length === 1 ? "campaign" : "campaigns"} · ${days}-day window`}
        />
        <DataTable
          columns={[
            { key: "campaign",    label: "Campaign" },
            { key: "clicks",      label: "Clicks",      align: "right",
              render: (r) => r.clicks.toLocaleString() },
            { key: "impressions", label: "Impressions", align: "right",
              render: (r) => r.impressions.toLocaleString() },
            { key: "spend",       label: "Spend",       align: "right",
              render: (r) => `$${r.spend.toFixed(2)}` },
            { key: "conversions", label: "Conv.",       align: "right" },
            { key: "phoneCalls",  label: "Calls",       align: "right",
              render: (r) => (r.phoneCalls ?? 0).toLocaleString(),
              // Emphasize the call column so the client's "calls from ads"
              // story reads at a glance — gray when zero, accent when non-zero.
              colorFn: (r) => (r.phoneCalls > 0 ? "var(--color-accent)" : undefined) },
          ]}
          rows={campaigns}
        />
        {conversionsNote && (
          <div
            className="px-5 py-2.5 border-t border-border-light text-[11px] text-text-muted"
            role="note"
          >
            {conversionsNote}
          </div>
        )}
      </Card>

      {insights?.[0] && <InsightCard>{insights[0].content}</InsightCard>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader title="Top Keywords" subtitle={`Search queries · ${days} days`} />
          <DataTable
            columns={[
              { key: "term",   label: "Keyword" },
              { key: "clicks", label: "Clicks", align: "right" },
              { key: "cost",   label: "Cost",   align: "right",
                render: (r) => `$${r.cost.toFixed(2)}` },
              { key: "signal", label: "Signal" },
            ]}
            rows={keywords}
          />
        </Card>

        <Card>
          <CardHeader title="Ad Clicks by City" subtitle="Geographic breakdown" />
          <DataTable
            columns={[
              { key: "city",   label: "City" },
              { key: "clicks", label: "Clicks", align: "right" },
              { key: "spend",  label: "Spend",  align: "right",
                render: (r) => `$${r.spend.toFixed(2)}` },
            ]}
            rows={adCities}
          />
        </Card>
      </div>

      {insights?.[1] && <InsightCard>{insights[1].content}</InsightCard>}
    </div>
  );
}

/**
 * Per-campaign summary card shown above the Daily Spend chart.
 * Always shows the hard totals (spend, clicks) so the numbers land even
 * when daily granularity from Windsor is missing or flat. share-of-spend
 * bar is accent-colored and widths to the campaign's percentage share.
 */
function CampaignSummaryCard({ campaign }) {
  const { campaign: name, spend, dailyAvg, clicks, cpc, shareOfSpend } = campaign;
  return (
    <Card className="px-5 py-4">
      <div className="text-[12px] font-semibold text-text-primary leading-snug">
        {name}
      </div>
      <div className="flex items-baseline gap-2 mt-2">
        <div className="text-[22px] font-semibold text-text-primary leading-none">
          ${spend.toLocaleString(undefined, { maximumFractionDigits: 2 })}
        </div>
        <div className="text-[11px] text-text-muted">
          ${dailyAvg.toFixed(2)} / day
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-border-light">
        <StatCell label="Clicks" value={clicks.toLocaleString()} />
        <StatCell label="Avg CPC" value={`$${cpc.toFixed(2)}`} />
      </div>
      <div className="mt-3">
        <div className="flex justify-between text-[10px] uppercase text-text-muted font-medium mb-1"
             style={{ letterSpacing: "0.05em" }}>
          <span>Share of Spend</span>
          <span>{shareOfSpend}%</span>
        </div>
        <div className="h-1.5 bg-border-light rounded-full overflow-hidden">
          <div
            className="h-full bg-accent"
            style={{ width: `${Math.min(100, shareOfSpend)}%` }}
          />
        </div>
      </div>
    </Card>
  );
}

function StatCell({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-text-muted font-medium"
           style={{ letterSpacing: "0.05em" }}>
        {label}
      </div>
      <div className="text-[13px] font-semibold text-text-primary mt-0.5">
        {value}
      </div>
    </div>
  );
}
